/**
 * The one way every surface names a session plan (ADR 0033, one session, one plan).
 *
 * A plan on a session is named by its session: `Tue, Sep 23 · {topic}` in the
 * course zone. A plan on no session is named by its topic. The Session plan card
 * gives a plan on an unlabelled session the placeholder topic `Session on {date}`;
 * a name built from it would say the date twice, so that plan is named by its
 * date alone. The placeholder and the check against it live only here.
 */
import { formatInCourseZone } from "@/lib/session-portal";

/** The day a session meets, in the course zone: `Tue, Sep 23`. */
export function classDay(startsAt: string, timezone: string): string {
  return formatInCourseZone(startsAt, timezone, { weekday: "short", month: "short", day: "numeric" });
}

/** The clock time a session starts, in the course zone: `6:00 PM`. */
export function classTime(startsAt: string, timezone: string): string {
  return formatInCourseZone(startsAt, timezone, { hour: "numeric", minute: "2-digit" });
}

/** The topic the Session plan card gives a new plan on a session with no label. */
export function placeholderTopic(startsAt: string, timezone: string): string {
  return `Session on ${formatInCourseZone(startsAt, timezone, { dateStyle: "medium" })}`;
}

/** A plan's own words: its topic, unless that is only the placeholder repeating its session's date. */
function ownTopic(topic: string | null | undefined, startsAt: string | null, timezone: string): string | null {
  const words = topic?.trim();
  if (!words) return null;
  return startsAt && words === placeholderTopic(startsAt, timezone) ? null : words;
}

/** A plan's name: `{day} · {topic}` on a session, the day alone for a placeholder topic, the topic on no session. */
export function planName(plan: { topic: string; starts_at: string | null }, timezone: string): string {
  if (!plan.starts_at) return plan.topic;
  const day = classDay(plan.starts_at, timezone);
  const topic = ownTopic(plan.topic, plan.starts_at, timezone);
  return topic ? `${day} · ${topic}` : day;
}

/** Plans in calendar order: by their session's start, then plans on no session in creation order. */
export function inCalendarOrder<T extends { session_starts_at: string | null; position: number }>(plans: T[]): T[] {
  return [...plans].sort((left, right) => {
    if (left.session_starts_at !== right.session_starts_at) {
      if (left.session_starts_at === null) return 1;
      if (right.session_starts_at === null) return -1;
      const order = Date.parse(left.session_starts_at) - Date.parse(right.session_starts_at);
      if (order !== 0) return order;
    }
    return left.position - right.position;
  });
}

/** A course unit's name, by the helper above. */
export function unitPlanName(unit: { topic: string; session_starts_at: string | null }, timezone: string): string {
  return planName({ topic: unit.topic, starts_at: unit.session_starts_at }, timezone);
}
